/**
 * Kingdom Factor Collective - Main Page
 * - Hero section
 * - Featured articles carousel
 * - Category filter tabs
 * - Article grid with pagination
 */

import { useState, useCallback, useRef, useEffect } from "react"
import { Link, router } from "@inertiajs/react"
import { Search, X } from "lucide-react"
import { SeoHead } from "@/components/SeoHead"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Header } from "@/components/layout/Header"
import { Footer } from "@/components/layout/Footer"
import {
  ArticleCard,
  CategoryTabs,
  FeaturedCarousel,
  Pagination,
  ViewToggle,
} from "./components"
import { USMapView } from "./components/USMapView"

export default function Index({
  seo,
  featuredArticles = [],
  articles = [],
  categories = [],
  pagination = {},
  mapLocations = [],
  mapArticles = [],
  activeQuery = null,
  settings = {},
}) {
  const [activeView, setActiveView] = useState("list")
  const [searchValue, setSearchValue] = useState(activeQuery || "")
  const { scheduleCallUrl = "#" } = settings
  const debounceRef = useRef(null)
  const isSearching = !!activeQuery

  // Calculate total article count from all categories
  const totalCount = categories.reduce((sum, cat) => sum + (cat.articleCount || 0), 0)

  // Sync search value when activeQuery prop changes (e.g. browser back/forward)
  useEffect(() => {
    setSearchValue(activeQuery || "")
  }, [activeQuery])

  const performSearch = useCallback((query) => {
    if (query) {
      router.get("/collective", { q: query }, { preserveState: true, preserveScroll: true })
    } else {
      // Explicitly navigate to clean URL to strip any leftover query params
      router.get("/collective", {}, { preserveState: false, preserveScroll: true })
    }
  }, [])

  const handleSearchChange = useCallback((e) => {
    const value = e.target.value
    setSearchValue(value)

    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      performSearch(value)
    }, 300)
  }, [performSearch])

  const clearSearch = useCallback(() => {
    setSearchValue("")
    if (debounceRef.current) clearTimeout(debounceRef.current)
    performSearch("")
  }, [performSearch])

  return (
    <>
      <SeoHead seo={seo} />

      <div className="bg-white text-zinc-900 min-h-screen">
        <Header activePage="collective" scheduleCallUrl={scheduleCallUrl} />

        {/* Hero Section */}
        <section className="bg-zinc-50 pt-32 pb-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
            <p className="text-kf-red font-semibold uppercase tracking-wider mb-4">
              Stories & Insights
            </p>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-zinc-900 mb-6">
              KINGDOM FACTOR <span className="text-kf-red">COLLECTIVE</span>
            </h1>
            <p className="text-zinc-600 text-lg max-w-2xl mx-auto">
              Building the world's largest collection of Christian business leadership stories—equipping, inspiring, and glorifying God in the marketplace.
            </p>
          </div>
        </section>

        {/* Featured Articles Carousel */}
        {!isSearching && featuredArticles.length > 0 && (
          <section className="bg-zinc-50 pb-16">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
              <FeaturedCarousel articles={featuredArticles} />
            </div>
          </section>
        )}

        {/* All Articles Section */}
        <section className="bg-zinc-50 py-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            {/* Category Tabs & View Toggle */}
            <div className="mb-12 space-y-4">
              <CategoryTabs
                categories={categories}
                activeSlug={null}
                totalCount={totalCount}
              />
              {!isSearching && (
                <ViewToggle activeView={activeView} onViewChange={setActiveView} />
              )}
            </div>

            {/* Search Bar */}
            <div className="mb-8 max-w-xl mx-auto">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <Input
                  type="text"
                  placeholder="Search by title, author, interviewee, city, or state..."
                  value={searchValue}
                  onChange={handleSearchChange}
                  className="pl-10 pr-10 bg-white border-zinc-200"
                />
                {searchValue && (
                  <button
                    onClick={clearSearch}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {isSearching || activeView === "list" ? (
              <>
                {/* Articles Grid */}
                {articles.length > 0 ? (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                    {articles.map((article) => (
                      <ArticleCard key={article.id} article={article} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    {isSearching ? (
                      <>
                        <p className="text-zinc-500 mb-2">
                          No articles found matching "{activeQuery}".
                        </p>
                        <button
                          onClick={clearSearch}
                          className="text-kf-red font-semibold hover:underline"
                        >
                          Clear search
                        </button>
                      </>
                    ) : (
                      <p className="text-zinc-500">No articles found.</p>
                    )}
                  </div>
                )}

                {/* Pagination */}
                <Pagination pagination={pagination} baseUrl="/collective" />
              </>
            ) : (
              <USMapView locations={mapLocations} articles={mapArticles} />
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-kf-gradient py-16">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="font-display text-2xl md:text-3xl text-white mb-4">
              WANT TO SHARE YOUR STORY?
            </h2>
            <p className="text-white/80 mb-8">
              Join our community of faith-driven leaders and share how God is working in your business.
            </p>
            <Button variant="kf-secondary" asChild>
              <Link href="/share-your-story">Get Started</Link>
            </Button>
          </div>
        </section>

        <Footer activePage="collective" />
      </div>
    </>
  )
}
